/**
 * @file settings.ts
 *
 * Shop settings API for the admin. Exposes the active currency symbol, which the
 * product list, cards, and detail drawer use to format prices. The symbol is
 * derived from the shop's ISO-4217 currency code read from `GET /v1/shops/settings`
 * (via {@link getShopCurrency}, which caches the settings for the session); the
 * legacy WooCommerce `/shop/settings/currency` route no longer exists on the
 * commerce-engine. Kept minimal here; later phases extend the settings surface.
 */

import { getShopCurrency } from './shop';

/**
 * Derives the narrow currency symbol (e.g. `"$"`, `"€"`, `"£"`) for an ISO-4217
 * currency code via `Intl.NumberFormat`.
 *
 * @param code - The ISO-4217 currency code (e.g. `"EUR"`).
 *
 * @returns The narrow symbol, or an empty string when the code is invalid or
 *   yields no symbol part.
 */
function symbolForCurrencyCode(code: string): string {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? '';
  } catch {
    // Intl throws (RangeError) on an empty/invalid code; the symbol is decorative,
    // so fall back to empty and let the store keep its default.
    return '';
  }
}

/**
 * Fetches the store's active currency symbol (e.g. `"$"`, `"€"`), derived from the
 * shop's ISO-4217 currency code on `GET /v1/shops/settings`.
 *
 * @returns The currency symbol, or an empty string when it cannot be derived.
 *
 * @throws {WcApiError} When the settings request fails.
 */
export async function fetchCurrencySymbol(): Promise<string> {
  const code = await getShopCurrency();
  return symbolForCurrencyCode(code);
}
